import { Component, OnInit, OnDestroy, ViewChild, ElementRef, AfterViewInit } from '@angular/core';
import {
  Account,
  AccountService,
  AuditService,
  ChannelName,
  Constants,
  CookieService,
  CustomValidators,
  CustomerSubscription,
  FormValidationService,
  GenericObject,
  Utils,
  ConfigService,
} from 'ngx-web-api';
import { SmsModalContext } from './sms-modal.context';
import { faXmark } from '@fortawesome/pro-light-svg-icons';
import { TranslateService } from '@ngx-translate/core';
import { ViewportService } from 'app/core/services/viewport.service';
import { Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { SmsModalService } from './sms-modal.service';
import { ModalService } from 'app/core/services/modal.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ErrorsService } from 'app/core/services/errors.service';

@Component({
  selector: 'fts-sms-modal',
  templateUrl: './sms-modal.component.html',
  styleUrls: ['./sms-modal.component.scss'],
})
export class SmsModalComponent implements OnInit, AfterViewInit, OnDestroy {
  private context: SmsModalContext;
  private subscriptions: Subscription[] = [];

  isUserInstantiated: boolean;
  colapsed = true;
  faXmark = faXmark;
  phoneMask = Constants.PHONE_MASK;
  account: Account = null;
  fontSize = 4;
  isMobile = false;
  loadingSub: Subscription;

  title = '';
  description = '';
  imgSrc = '';
  imageLoaded = false;
  error = '';

  form: FormGroup;
  private formErrors: GenericObject<string[]> = {};
  private serverErrors: GenericObject<string[]> = {};
  private showValidationErrors = false;

  @ViewChild('couponTitle') couponTitle!: ElementRef;
  @ViewChild('smsModalLeftContainer') smsModalLeftContainerRef?: ElementRef;

  private validationMessages: GenericObject<GenericObject<string>> = {
    name: {
      required: this.translateService.instant('component.sms_sub_promo_modal.name_required'),
    },
    phone: {
      required: this.translateService.instant('component.sms_sub_promo_modal.phone_required'),
      phone: this.translateService.instant('component.sms_sub_promo_modal.phone_invalid'),
    },
    email: {
      email: this.translateService.instant('component.sms_sub_promo_modal.email_invalid'),
      required: this.translateService.instant('component.sms_sub_promo_modal.email_required'),
    },
    password: {
      required: this.translateService.instant('component.sms_sub_promo_modal.password_required'),
    },
  };

  constructor(
    private accountService: AccountService,
    public translateService: TranslateService,
    private viewPortService: ViewportService,
    private cookieService: CookieService,
    private configService: ConfigService,
    private smsModalService: SmsModalService,
    private modalService: ModalService,
    private formBuilder: FormBuilder,
    private validationService: FormValidationService,
    private errorService: ErrorsService,
    private auditService: AuditService
  ) {
    this.subscriptions = [
      this.accountService.account$.subscribe(user => {
        this.isUserInstantiated = user.isInstantiated;
        this.account = user.copy();
      }),
      this.viewPortService.viewport$.subscribe(viewport => {
        this.isMobile = viewport.isMobile;
      }),
      this.smsModalService.fetchCoupon().subscribe(res => {
        this.title = res.coupon?.displayName;
        if (res.coupon?.displayName) this.setCouponTitleFontSize();
        this.description = res.coupon?.description;
        this.imgSrc = res.coupon?.imageUrl;
        if (!!this.imgSrc) {
          this.colapsed = window.innerWidth < 900;
        } else {
          this.colapsed = true;
        }
      }),
    ];
    this.buildForm();
  }

  private buildForm(): void {
    const controls = {
      name: ['', !this.isUserInstantiated ? [Validators.required] : []],
      password: ['', this.isUserInstantiated && !this.account.phone ? [Validators.required] : []],
      phone: [!!this.account.phone ? this.account.phone : '', !this.account.phone ? [Validators.required, CustomValidators.phone] : []],
      email: ['', !this.isUserInstantiated ? [CustomValidators.email, Validators.required] : []],
      emailSubscription: [true],
    };
    this.form = this.formBuilder.group(controls);
    this.form.valueChanges.subscribe(() => this.validateForm());
    this.validateForm();
  }

  imageLoadedEvent(): void {
    this.imageLoaded = true;
  }

  ngOnInit(): void {
    this.accountService.account$
      .pipe(
        filter((account: Account) => !!account && account.isInstantiated),
        take(1)
      )
      .subscribe((account: Account) => {
        this.account = account;
        this.isUserInstantiated = account.isInstantiated;
        this.buildForm();
        this.updateFontSize();
      });

    this.auditService.createAudit(() => 'SMS Subscription Offer modal opened');

    this.configService.storeConfig$.pipe(take(1)).subscribe(config => {
      const expireDays = config?.daysAfterSmsOfferSubsPopupAppears;
      this.cookieService.setSmsAcknowledgementCookie(expireDays);
    });
  }

  ngAfterViewInit() {
    setTimeout(() => {
      if (document.activeElement === this.smsModalLeftContainerRef?.nativeElement) {
        this.smsModalLeftContainerRef?.nativeElement.blur();
      }
    });
  }

  setCouponTitleFontSize(): void {
    setTimeout(() => {
      if (this.couponTitle.nativeElement.offsetHeight > 296) {
        this.couponTitle.nativeElement.style.setProperty('font-size', '3.25rem');
      }
    });
  }

  ngOnDestroy(): void {
    for (const sub of this.subscriptions) {
      sub?.unsubscribe();
    }
  }

  close(): void {
    if (!!this.context['ref']) {
      this.auditService.createAudit(() => 'SMS Subscription Offer modal dismissed');
      this.context['ref'].close();
    }
  }

  noThanksClicked(): void {
    this.auditService.createAudit(() => "SMS Subscription Offer modal 'No, Thanks' button clicked");
    this.close();
  }

  private validateForm() {
    this.serverErrors = undefined;
    this.formErrors = this.validationService.validateForm(this.form, this.validationMessages);
  }

  get errors(): GenericObject<string[]> {
    return this.showValidationErrors ? Utils.mergeGenericObjects(this.formErrors, this.serverErrors) : {};
  }

  submitClicked(): void {
    this.formErrors = undefined;
    this.serverErrors = undefined;
    this.showValidationErrors = true;
    this.form.updateValueAndValidity();
    this.handleFormValidationFocus();
    if (!this.form.valid) {
      const fieldsMessages: string[] = [];

      Object.keys(this.form.controls).forEach(key => {
        if (!this.form.get(key).valid) {
          fieldsMessages.push(`${key} is missing. `);
        } else {
          fieldsMessages.push(`${key}: ${this.form.get(key).value}. `);
        }
      });

      if (fieldsMessages.length) {
        this.auditService.createAudit(() => `SMS Subscription Offer failed. ${fieldsMessages.join(' ')}`);
      }

      return;
    }

    this.auditService.createAudit(() => 'SMS Modal Submit Button Clicked');

    if (this.isUserInstantiated) {
      if (!this.account.phone) {
        this.updateAccountsPhone();
      } else {
        this.subscribeInstansiatedUser();
      }
    } else {
      this.subscribeGuestUser();
    }
  }

  private handleFormValidationFocus() {
    if (this.errors?.name?.length) {
      document.getElementById('sms-modal-name')?.focus();
    } else if (this.errors?.email?.length) {
      document.getElementById('sms-modal-email')?.focus();
    } else if (this.errors?.phone?.length) {
      document.getElementById('sms-modal-phone')?.focus();
    } else if (this.errors?.password?.length) {
      document.getElementById('sms-modal-password')?.focus();
    }
  }

  private subscribeGuestUser(): void {
    let subs = this.smsModalService.addOffersService(this.smsModalService.addSmsChannel([]));

    if (this.form.get('emailSubscription').value && this.form.get('email').value) {
      subs = this.smsModalService.addEmailChannel(subs);
      subs = this.smsModalService.addEmailOffersService(subs);
    }
    let formatedPhone = this.dashedPhoneFormater(this.form.get('phone').value);

    const fullName = this.form.get('name').value;

    this.loadingSub = this.accountService
      .updateGuestCustomerSubcriptions(subs, fullName, this.form.get('email').value, formatedPhone)
      .subscribe(
        res => this.handleSuccess(res),
        error => this.handleError(error)
      );
  }

  private updateAccountsPhone(): void {
    const updatedAccount = this.account
      .copy()
      .deserialize({ phone: this.form.get('phone').value, password: this.form.get('password').value });
    this.loadingSub = this.accountService.updateAccount(updatedAccount).subscribe(
      () => this.subscribeInstansiatedUser(),
      error => this.handleError(error)
    );
  }

  private subscribeInstansiatedUser(): void {
    let updatedSubscriptions = [];
    if (this.account.subscriptions.findIndex(sub => sub.channelName === ChannelName.SMS) === -1) {
      //Channel doesn't exist
      updatedSubscriptions = this.smsModalService.addOffersService(this.smsModalService.addSmsChannel([...this.account.subscriptions]));
    } else {
      updatedSubscriptions = this.smsModalService.addOffersService([...this.account.subscriptions]);
    }

    if (this.form.get('emailSubscription').value) {
      updatedSubscriptions = this.smsModalService.addEmailOffers(updatedSubscriptions);
    }

    this.loadingSub = this.accountService.updateCustomerSubscriptions(updatedSubscriptions).subscribe((result: CustomerSubscription[]) => {
      this.handleSuccess(result);
    });
    this.subscriptions.push(this.loadingSub);
  }

  private handleSuccess(response: CustomerSubscription[]): void {
    if (typeof response === typeof [CustomerSubscription] && this.accountService.checkedIfSmsOffersChannelSubscribed(response)) {
      this.modalService
        .openSuccessNotificationModal(this.translateService.instant('component.sms_sub_promo_modal.thanks_for_sub'))
        .subscribe(res => {
          this.auditService.createAudit(
            () =>
              `SMS Subscription Offer Successful. Name: ${this.form.get('name').value}, Email: ${
                this.form.get('email').value
              }, Phone Number: ${this.form.get('phone').value}`
          );
          this.close();
        });
    } else {
      this.modalService
        .openErrorNotificationModal(
          this.translateService.instant('component.sms_sub_promo_modal.something_wrong'),
          this.translateService.instant('component.sms_sub_promo_modal.something_wrong_details')
        )
        .subscribe(() => this.auditService.createAudit(() => 'Something went wrong submitting SMS Modal'));
    }
  }

  private handleError(err: any) {
    this.auditService.createAudit(() => 'Something went wrong submitting SMS Modal');

    if (err.status === 409 && err.error && err.error.message === 'It seems that you are already subscribed for SMS') {
      this.modalService.openSuccessNotificationModal(err.error.message).subscribe(() => {
        this.auditService.createAudit(() => 'User already subscribed to SMS');
        this.close();
      });
      return;
    }
    if (err.status === 401 || err.status === 409) {
      if (err.error.message.includes('Password')) {
        this.serverErrors = { password: [err.error.message] };
      } else if (err.error.message.includes('phone')) {
        this.serverErrors = { phone: [err.error.message] };
      }
    } else {
      const parsedError = this.errorService.parseErrorResponse(err);
      if (!!parsedError?.errors['cellPhone']) {
        parsedError.errors['phone'] = parsedError.errors['cellPhone'];
      }
      this.serverErrors = parsedError?.errors;
    }
  }

  private updateFontSize(): void {
    if (this.title.length <= 8) {
      this.fontSize = this.isMobile ? 3 : 4;
    } else if (8 < this.title.length && this.title.length <= 16) {
      this.fontSize = this.isMobile ? 2 : 3;
    } else if (16 < this.title.length && this.title.length <= 40) {
      this.fontSize = this.isMobile ? 1.5 : !this.colapsed ? 1.8 : 2;
    } else {
      this.fontSize = this.isMobile ? 1 : 2;
    }
  }

  private dashedPhoneFormater(value: string): string {
    let formatedPhone = '';
    for (let c of value) {
      if (c != '(' && c != ')') {
        if (c == ' ') {
          formatedPhone += '-';
        } else {
          formatedPhone += c;
        }
      }
    }
    return formatedPhone;
  }
}
